import { NavLink, useLocation, useNavigationType } from "react-router-dom";
import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";

const navItems = [
  { to: "/", label: "Pantry", icon: PantryIcon },
  { to: "/add", label: "Add", icon: PlusIcon },
  { to: "/list", label: "List", icon: ListIcon },
  { to: "/settings", label: "Settings", icon: GearIcon },
];

export function Layout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  useScrollRestoration(mainRef);
  const headerTitle = navItems.find((n) =>
    n.to === "/" ? pathname === "/" : pathname.startsWith(n.to)
  )?.label;

  return (
    <div className="flex h-full flex-col">
      <header className="sticky top-0 z-10 flex h-14 items-center justify-between border-b border-stone-200 bg-white px-4 dark:border-stone-800 dark:bg-stone-900">
        <div className="flex items-center gap-2">
          <div className="grid h-7 w-7 place-items-center rounded-md bg-brand-600 text-white">
            <PantryIcon className="h-4 w-4" />
          </div>
          <h1 className="text-base font-semibold">{headerTitle ?? "Pantry"}</h1>
        </div>
      </header>

      <main ref={mainRef} className="flex-1 overflow-y-auto pb-32">{children}</main>

      <BackToTopButton scrollRef={mainRef} />

      <nav
        className="fixed bottom-0 left-0 right-0 z-10 border-t border-stone-200 bg-white dark:border-stone-800 dark:bg-stone-900"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="mx-auto flex max-w-2xl">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `flex flex-1 flex-col items-center gap-0.5 py-2.5 text-xs font-medium transition-colors ${
                  isActive
                    ? "text-brand-700 dark:text-brand-400"
                    : "text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200"
                }`
              }
            >
              <item.icon className="h-5 w-5" />
              {item.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

// Restores <main>'s scrollTop after browser back / `navigate(-1)` (POP) and
// resets to 0 on forward navigation (PUSH/REPLACE). The scrollable element
// here is <main>, NOT the window — see Layout — so the browser's native
// scroll restoration and React Router's <ScrollRestoration> both miss it.
//
// Positions are kept in an in-memory Map keyed by location.key (React
// Router's stable per-history-entry id). Lost on hard reload, which matches
// typical SPA expectations.
function useScrollRestoration(scrollRef: RefObject<HTMLElement>) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const positions = useRef<Map<string, number>>(new Map());
  const currentKey = useRef<string>(location.key);

  // Continuously track the current scroll position under the active key, so
  // that whenever the user navigates we already have a fresh value to restore
  // to. Coalesced via rAF to avoid thrashing the Map on every scroll event.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let rafId = 0;
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        positions.current.set(currentKey.current, el.scrollTop);
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [scrollRef]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    currentKey.current = location.key;

    if (navigationType === "POP") {
      const saved = positions.current.get(location.key) ?? 0;
      // Pages fetch data on mount and grow as content arrives. We can't use
      // ResizeObserver to detect that growth — <main> is flex-1 with
      // overflow:auto, so its own box never resizes; only its scrollHeight
      // does, which RO doesn't observe. Poll per frame instead until the
      // saved position is reachable, then disconnect. Bail on real user
      // scroll interaction (`touchmove`, not `touchstart` — tapping a row
      // after navigation fires touchstart and would otherwise abort
      // restoration before async content lands on mobile).
      const deadline = performance.now() + 4000;
      let rafId = 0;
      let stopped = false;
      const stop = () => {
        if (stopped) return;
        stopped = true;
        if (rafId) cancelAnimationFrame(rafId);
        el.removeEventListener("wheel", stop);
        el.removeEventListener("touchmove", stop);
      };
      const tick = () => {
        if (stopped) return;
        if (el.scrollHeight >= saved + el.clientHeight) {
          el.scrollTop = saved;
          if (Math.abs(el.scrollTop - saved) <= 2) {
            stop();
            return;
          }
        }
        if (performance.now() > deadline) {
          stop();
          return;
        }
        rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
      el.addEventListener("wheel", stop, { once: true, passive: true });
      el.addEventListener("touchmove", stop, { once: true, passive: true });
      return stop;
    }
    el.scrollTop = 0;
  }, [location.key, navigationType, scrollRef]);
}

function BackToTopButton({ scrollRef }: { scrollRef: RefObject<HTMLElement> }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let rafId = 0;
    const update = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        setVisible(el.scrollTop > 600);
      });
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    return () => {
      el.removeEventListener("scroll", update);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [scrollRef]);

  if (!visible) return null;

  return (
    <button
      type="button"
      onClick={() =>
        scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" })
      }
      className="fixed bottom-20 left-4 z-10 grid h-12 w-12 place-items-center rounded-full bg-stone-700/90 text-white shadow-lg backdrop-blur transition-opacity hover:bg-stone-800 dark:bg-stone-200/90 dark:text-stone-900 dark:hover:bg-white"
      aria-label="Back to top"
      title="Back to top"
    >
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.5">
        <path d="M12 19V5M5 12l7-7 7 7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

function PantryIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M4 9h16M4 15h16" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v8M8 12h8" strokeLinecap="round" />
    </svg>
  );
}

function ListIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 6h11M9 12h11M9 18h11" strokeLinecap="round" />
      <circle cx="4.5" cy="6" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="4.5" cy="12" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="4.5" cy="18" r="1.2" fill="currentColor" stroke="none" />
    </svg>
  );
}

function GearIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3 1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8 1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </svg>
  );
}
